import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { FloatingLabelInput } from '@/shared/components/custom/form-field/FloatingLabelInput';
import { ColorPickerPopover } from '@/shared/components/custom/color-picker/ColorPickerPopover';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { cn } from '@/core/lib/utils';
import { resolveColorValue } from '@/core/lib/color-palette';
import { useDeleteFieldOption } from '../hooks/useProjectFieldOptionMutations';
import type { FieldOptionInput } from '../schemas';

interface FieldOptionEditorProps {
  value: FieldOptionInput[];
  onChange: (options: FieldOptionInput[]) => void;
  disabled?: boolean;
  /** Present only when editing an existing field — enables instant delete for persisted options. */
  projectId?: string;
  fieldId?: string;
}

export function FieldOptionEditor({
  value,
  onChange,
  disabled = false,
  projectId,
  fieldId,
}: FieldOptionEditorProps) {
  const { t } = useTranslation('customFields');
  const deleteOption = useDeleteFieldOption(projectId ?? '', fieldId ?? '');
  const { confirm, isOpen, options: confirmOptions, handleConfirm, handleCancel } = useConfirm();

  const handleAdd = () => {
    onChange([...value, { name: '', value: '', color: undefined, orderIndex: value.length }]);
  };

  const removeLocally = (index: number) => {
    const updated = value
      .filter((_, i) => i !== index)
      .map((opt, i) => ({ ...opt, orderIndex: i }));
    onChange(updated);
  };

  const handleRemove = async (index: number) => {
    const target = value[index];

    // A persisted option (has a backend id) is a real, irreversible DELETE — confirm first.
    // A new, unsaved option only needs the local array update, no backend call involved.
    if (target.id && projectId && fieldId) {
      const { confirmed } = await confirm({
        title: t('options.confirmDeleteTitle'),
        description: t('options.confirmDeleteDescription'),
        variant: 'destructive',
      });
      if (!confirmed) return;
      removeLocally(index);
      deleteOption.mutate(target.id);
      return;
    }

    removeLocally(index);
  };

  const handleChange = (
    index: number,
    field: keyof FieldOptionInput,
    newValue: string | number | undefined,
  ) => {
    const updated = value.map((opt, i) => {
      if (i !== index) return opt;
      const next = { ...opt, [field]: newValue };
      if (field === 'name') next.value = newValue as string;
      return next;
    });
    onChange(updated);
  };

  return (
    <div className="space-y-0.5">
      {isOpen && confirmOptions && (
        <ConfirmDialog
          open={isOpen}
          onOpenChange={(open) => !open && handleCancel()}
          title={confirmOptions.title}
          description={confirmOptions.description}
          confirmLabel={confirmOptions.confirmLabel}
          cancelLabel={confirmOptions.cancelLabel}
          variant={confirmOptions.variant}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}

      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
        {t('options.sectionTitle')}
      </p>

      <div
        data-testid="custom-fields-option-list"
        className={cn(disabled && 'opacity-50 pointer-events-none')}
      >
        {value.map((option, index) => (
          <div key={index} className="group flex items-center gap-2 rounded-md px-2 py-1.5">
            <ColorPickerPopover
              value={option.color}
              onSelect={(token) => handleChange(index, 'color', token)}
              disabled={disabled}
              trigger={
                <button
                  type="button"
                  className="h-5 w-5 shrink-0 rounded-full border border-input"
                  style={{ background: resolveColorValue(option.color) ?? '#9333ea' }}
                  aria-label={t('options.color')}
                  data-testid={`custom-fields-option-color-${index}`}
                />
              }
            />

            <FloatingLabelInput
              id={`option-name-${index}`}
              label={t('options.namePlaceholder')}
              value={option.name}
              hasValue={!!option.name}
              onChange={(e) => handleChange(index, 'name', (e.target as HTMLInputElement).value)}
              disabled={disabled}
              className="flex-1"
              data-testid={`custom-fields-option-name-${index}`}
              endElement={
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => handleRemove(index)}
                      disabled={disabled}
                      data-testid={`custom-fields-option-remove-${index}`}
                      aria-label={t('options.remove')}
                      className="invisible group-hover:visible rounded p-1 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{t('options.remove')}</TooltipContent>
                </Tooltip>
              }
            />
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={handleAdd}
        disabled={disabled}
        data-testid="custom-fields-add-option-button"
        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
      >
        <Plus className="h-4 w-4 shrink-0" />
        {t('options.addOption')}
      </button>
    </div>
  );
}
